import React, { useState } from "react";
import Marquee from "../../utils/Marquee";
import { useArtistAwards } from "@/api/hooks/useArtistAwards";
import { useAwardsStore } from "@/stores/awardsStore";
import { X } from "lucide-react";
import { Loader } from "@/components/ui/Loader";
import MaskExactProfessionalName from "../../utils/MaskExactProfessionalName";

interface ArtistEconomyTrophyProps {
  enableDelete?: boolean;
}

const ArtistEconomyTrophy: React.FC<ArtistEconomyTrophyProps> = ({
  enableDelete = false,
}) => {
  const { awards } = useArtistAwards();
  const deleteItem = useAwardsStore((s) => s.deleteItem);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const allCards = [...awards, ...awards];

  return (
    <div className="w-full mt-20 overflow-hidden">
      <Marquee
        direction="left"
        speed={50}
        pauseOnHover
        className="min-h-[200px]"
      >
        <div className="flex flex-nowrap items-stretch gap-0 shrink-0 whitespace-normal">
          {allCards.map((item, index) => {
            const isRealAward = !item.isPlaceholder && typeof item.id === "number";
            const canDelete = enableDelete && isRealAward;
            const isDeleting = deletingId === item.id;

            const handleDelete = async () => {
              if (!canDelete || deletingId !== null) return;
              try {
                setDeletingId(item.id as number);
                await deleteItem(item.id as number);
              } finally {
                setDeletingId(null);
              }
            };

            return (
              <div
                key={`trophy-${item.id}-${index}`}
                className="flex items-center gap-2 w-80 min-w-80 min-h-24 bg-purple/20 border border-pink-600/20 bg-pink-900/10 p-3 rounded-xl mx-4 shrink-0"
              >
                <div className="w-10 h-10 flex-shrink-0">
                  <TrouphySVG />
                </div>
                <div className="min-w-0 flex-1 relative">
                  {canDelete && (
                    <button
                      type="button"
                      onClick={handleDelete}
                      disabled={isDeleting}
                      className="absolute -top-8 -right-2 z-50 flex h-6 w-6 items-center justify-center rounded-full text-xs text-white border border-white/40 disabled:opacity-60"
                    >
                      {isDeleting ? <Loader size="small" /> : <X className="h-3 w-3" />}
                    </button>
                  )}
                  <p
                    className={`text-sm font-light break-words whitespace-normal ${item.isPlaceholder ? "opacity-80 italic" : ""}`}
                  >
                    <MaskExactProfessionalName value={item.displayText} />
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </Marquee>
    </div>
  );
};

export default ArtistEconomyTrophy;

const TrouphySVG = () => {
  return (
    <svg
      viewBox="0 0 67 80"
      fill="none"
      width={40}
      height={40}
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M33.1354 66.2708C51.4356 66.2708 66.2708 51.4356 66.2708 33.1354C66.2708 14.8352 51.4356 0 33.1354 0C14.8352 0 0 14.8352 0 33.1354C0 51.4356 14.8352 66.2708 33.1354 66.2708Z"
        fill="#EBA206"
      />
      <path
        d="M33.1325 80L56.5625 56.5699L33.1325 33.1399L9.70246 56.57L33.1325 80Z"
        fill="#FFDD66"
      />
      <path
        d="M33.1346 60.149C48.0537 60.149 60.148 48.0546 60.148 33.1355C60.148 18.2164 48.0537 6.12207 33.1346 6.12207C18.2154 6.12207 6.12109 18.2164 6.12109 33.1355C6.12109 48.0546 18.2154 60.149 33.1346 60.149Z"
        fill="white"
      />
      <g filter="url(#filter0_d_936_11196)">
        <rect
          x="31.5039"
          y="33.0413"
          width="1.65007"
          height="5.64435"
          fill="url(#paint0_linear_936_11196)"
        />
        <path
          d="M25.2453 21.1261H20.1562C20.1563 24.9543 20.4589 27.1564 26.0817 27.1564"
          stroke="#FFDD66"
          strokeWidth="1.88399"
        />
        <path
          d="M39.7718 26.1399C39.7718 30.1439 36.4256 33.3898 32.298 33.3898C28.1703 33.3898 24.8242 30.1439 24.8242 26.1399C24.8242 22.1359 28.1703 18.89 32.298 18.89C36.4256 18.89 39.7718 22.1359 39.7718 26.1399Z"
          fill="#FFDD66"
        />
        <path
          d="M32.298 38.4037C29.2505 38.4037 26.78 39.2683 26.78 40.3348H37.816C37.816 39.2683 35.3455 38.4037 32.298 38.4037Z"
          fill="#FFDD66"
        />
        <path
          d="M24.8242 17.4967H39.7718V25.8011H24.8242V17.4967Z"
          fill="#FFDD66"
        />
        <path
          d="M37.816 40.7074H26.78V40.3009H37.816V40.7074Z"
          fill="#FFDD66"
        />
        <path
          d="M39.5594 21.1261H44.6484C44.6484 24.9543 44.3458 27.1564 38.723 27.1564"
          stroke="#DE9300"
          strokeWidth="1.88399"
        />
        <path
          d="M39.7683 26.14C39.7683 30.144 36.4222 33.3899 32.2946 33.3899C32.2946 33.3899 32.2946 30.144 32.2946 26.14C32.2946 22.136 32.2946 18.8901 32.2946 18.8901C36.4222 18.8901 39.7683 22.136 39.7683 26.14Z"
          fill="url(#paint1_linear_936_11196)"
        />
        <path
          d="M32.2946 38.4038C32.2946 38.4038 32.2946 39.2684 32.2946 40.3348H37.8126C37.8126 39.2684 35.3421 38.4038 32.2946 38.4038Z"
          fill="url(#paint2_linear_936_11196)"
        />
        <path
          d="M32.2946 17.4973H39.7683V25.8012H32.2946V17.4973Z"
          fill="url(#paint3_linear_936_11196)"
        />
        <path
          d="M37.8126 40.7075H32.2946C32.2946 40.7075 32.2822 40.4593 32.2946 40.301C32.4578 38.2166 37.8126 40.301 37.8126 40.301V40.7075Z"
          fill="url(#paint4_linear_936_11196)"
        />
        <ellipse
          cx="32.4413"
          cy="25.333"
          rx="4.47255"
          ry="4.33856"
          fill="#DCAE0C"
        />
        <path
          d="M37.5788 25.2795C37.5788 28.0133 35.29 30.254 32.4359 30.254C29.5819 30.254 27.2931 28.0133 27.2931 25.2795C27.2931 22.5458 29.5819 20.3051 32.4359 20.3051C35.29 20.3051 37.5788 22.5458 37.5788 25.2795Z"
          fill="#DE9300"
          stroke="#FFE176"
          strokeWidth="0.953378"
        />
        <path
          d="M32.4385 21.7126L33.5717 23.9111L35.838 24.1859L34.2818 25.8786L34.7048 28.3079L32.4385 27.2087L30.1722 28.3079L30.599 25.8786L29.0391 24.1859L31.3054 23.9111L32.4385 21.7126Z"
          fill="#FFF4BC"
        />
        <path
          d="M32.6246 21.6167L32.4385 21.2557L32.2525 21.6167L31.1701 23.7166L29.0139 23.9781L28.6082 24.0272L28.8851 24.3277L30.375 25.9444L29.966 28.2717L29.8952 28.6749L30.2636 28.4962L32.4385 27.4413L34.6135 28.4962L34.9812 28.6746L34.9111 28.272L34.5057 25.9444L35.9921 24.3275L36.2682 24.0272L35.8632 23.9781L33.707 23.7166L32.6246 21.6167Z"
          stroke="#C98500"
          strokeOpacity="0.7"
          strokeWidth="0.418666"
        />
        <path
          d="M23.1875 19.2703L23.9755 17.5878L23.1875 15.9054L24.9219 16.6698L26.6563 15.9054L25.8682 17.5878L26.6563 19.2703L24.9219 18.5058L23.1875 19.2703Z"
          fill="white"
        />
        <path
          d="M24.5156 43.56C24.5156 41.8049 25.9384 40.3821 27.6936 40.3821H36.9924C38.7475 40.3821 40.1703 41.8049 40.1703 43.56V48.1609H24.5156V43.56Z"
          fill="#243B6D"
        />
        <rect
          x="27.0897"
          y="42.7162"
          width="10.8353"
          height="3.35245"
          fill="#F9B50E"
          stroke="#FFEC86"
          strokeWidth="0.820059"
        />
        <path
          d="M27.5078 42.3062H32.7805L36.9431 46.4787H31.6704L27.5078 42.3062Z"
          fill="#FFEC86"
        />
        <rect
          x="23.6523"
          y="48.1606"
          width="17.3941"
          height="1.61516"
          fill="#DE9300"
        />
      </g>
      <defs>
        <filter
          id="filter0_d_936_11196"
          x="19.0508"
          y="15.7414"
          width="28.9992"
          height="36.4945"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity={0} result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset dx="1.14808" dy="1.14808" />
          <feGaussianBlur stdDeviation="0.656047" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.3 0"
          />
          <feBlend
            mode="normal"
            in2="BackgroundImageFix"
            result="effect1_dropShadow_936_11196"
          />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="effect1_dropShadow_936_11196"
            result="shape"
          />
        </filter>
        <linearGradient
          id="paint0_linear_936_11196"
          x1="32.3289"
          y1="33.0413"
          x2="32.3289"
          y2="38.6856"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#C07F00" />
          <stop offset={1} stopColor="#DE9300" />
        </linearGradient>
        <linearGradient
          id="paint1_linear_936_11196"
          x1="39.7169"
          y1="24.8084"
          x2="32.2242"
          y2="24.8084"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#DE9300" />
          <stop offset={1} stopColor="#FFBC11" />
        </linearGradient>
        <linearGradient
          id="paint2_linear_936_11196"
          x1="39.7169"
          y1="24.8084"
          x2="32.2242"
          y2="24.8084"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#DE9300" />
          <stop offset={1} stopColor="#FFBC11" />
        </linearGradient>
        <linearGradient
          id="paint3_linear_936_11196"
          x1="39.7169"
          y1="24.8084"
          x2="32.2242"
          y2="24.8084"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#DE9300" />
          <stop offset={1} stopColor="#FFBC11" />
        </linearGradient>
        <linearGradient
          id="paint4_linear_936_11196"
          x1="39.7169"
          y1="24.8084"
          x2="32.2242"
          y2="24.8084"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#DE9300" />
          <stop offset={1} stopColor="#FFBC11" />
        </linearGradient>
      </defs>
    </svg>
  );
};
